import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  SimpleGrid,
  Input,
  Button,
  useColorModeValue,
  InputGroup,
  InputRightElement,
  IconButton,
  FormControl,
  FormLabel,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { Card } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';

// Fallback plans, used only if httpInjectorService.getPlans() is unavailable
// or fails. Replace plan_id values with your real plan IDs.
const FALLBACK_PLANS = [
  {
    plan_id: 1,
    name: 'Free Trial',
    price: 0,
    period: '14 days',
    features: ['All core features', 'Up to 5 users', 'No credit card required'],
  },
  {
    plan_id: 2,
    name: 'Paid Plan',
    price: 29,
    period: 'per month',
    features: ['All core features', 'Unlimited users', 'Priority support'],
  },
];

const validationSchema = Yup.object({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
    .min(6, 'Min 6 characters')
    .required('Password is required'),
  website: Yup.string().max(255, 'Max 255 characters').required('Website is required'),
  company_name: Yup.string()
    .max(255, 'Max 255 characters')
    .required('Company name is required'),
  Address: Yup.string().required('Address is required'),
  country: Yup.string().required('Country is required'),
  plan_id: Yup.mixed().required('Please select a plan'),
});

export default function OrganizationRegister() {
  const [showPassword, setShowPassword] = useState(false);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [plansLoading, setPlansLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await httpInjectorService.getPlans();
        if (response.status === 'success' && Array.isArray(response.data) && response.data.length) {
          setPlans(response.data);
        }
      } catch (err) {
        // silently fall back to FALLBACK_PLANS
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await httpInjectorService.organizationRegister(values);
      if (response.status === 'success') {
        toast.success(response.message);
        navigate('/login');
      } else {
        toast.error(response.message || 'Registration failed');
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bgGradient="linear(135deg, purple.700 0%, pink.500 100%)"
      p={6}
    >
      <Card
        className="w-100 shadow-2xl rounded-4"
        style={{
          maxWidth: '900px',
          backdropFilter: 'blur(18px)',
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid rgba(255,255,255,0.15)',
          padding: '0',
        }}
      >
        <Box p={{ base: 6, md: 10 }}>
          <Text
            fontSize="3xl"
            fontWeight="bold"
            mb={2}
            textAlign="center"
            color="purple.700"
          >
            Register your organization
          </Text>
          <Text fontSize="sm" textAlign="center" color="gray.500" mb={8}>
            Create your organization account and choose a plan to get started
          </Text>

          <Formik
            initialValues={{
              first_name: '',
              last_name: '',
              email: '',
              password: '',
              website: '',
              company_name: '',
              Address: '',
              country: '',
              plan_id: '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              setFieldValue,
              values,
              errors,
              touched,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <VStack spacing={5} align="stretch">
                  {/* Name */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={touched.first_name && !!errors.first_name}>
                      <FormLabel fontSize="sm" color="gray.600">
                        First name
                      </FormLabel>
                      <Input
                        name="first_name"
                        placeholder="First name"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="filled"
                        bg="gray.100"
                        _focus={{ bg: 'white', borderColor: 'purple.400' }}
                      />
                      {touched.first_name && errors.first_name && (
                        <Text fontSize="sm" color="red.500">
                          {errors.first_name}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={touched.last_name && !!errors.last_name}>
                      <FormLabel fontSize="sm" color="gray.600">
                        Last name
                      </FormLabel>
                      <Input
                        name="last_name"
                        placeholder="Last name"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="filled"
                        bg="gray.100"
                        _focus={{ bg: 'white', borderColor: 'purple.400' }}
                      />
                      {touched.last_name && errors.last_name && (
                        <Text fontSize="sm" color="red.500">
                          {errors.last_name}
                        </Text>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  {/* Email + Password */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={touched.email && !!errors.email}>
                      <FormLabel fontSize="sm" color="gray.600">
                        Email
                      </FormLabel>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="filled"
                        bg="gray.100"
                        _focus={{ bg: 'white', borderColor: 'purple.400' }}
                      />
                      {touched.email && errors.email && (
                        <Text fontSize="sm" color="red.500">
                          {errors.email}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={touched.password && !!errors.password}>
                      <FormLabel fontSize="sm" color="gray.600">
                        Password
                      </FormLabel>
                      <InputGroup>
                        <Input
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          variant="filled"
                          bg="gray.100"
                          _focus={{ bg: 'white', borderColor: 'purple.400' }}
                        />
                        <InputRightElement>
                          <IconButton
                            size="sm"
                            variant="ghost"
                            onClick={() => setShowPassword(!showPassword)}
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            aria-label="toggle password"
                          />
                        </InputRightElement>
                      </InputGroup>
                      {touched.password && errors.password && (
                        <Text fontSize="sm" color="red.500">
                          {errors.password}
                        </Text>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  {/* Company + Website */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={touched.company_name && !!errors.company_name}>
                      <FormLabel fontSize="sm" color="gray.600">
                        Company name
                      </FormLabel>
                      <Input
                        name="company_name"
                        placeholder="Company name"
                        value={values.company_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="filled"
                        bg="gray.100"
                        _focus={{ bg: 'white', borderColor: 'purple.400' }}
                      />
                      {touched.company_name && errors.company_name && (
                        <Text fontSize="sm" color="red.500">
                          {errors.company_name}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={touched.website && !!errors.website}>
                      <FormLabel fontSize="sm" color="gray.600">
                        Website
                      </FormLabel>
                      <Input
                        name="website"
                        placeholder="https://yourcompany.com"
                        value={values.website}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="filled"
                        bg="gray.100"
                        _focus={{ bg: 'white', borderColor: 'purple.400' }}
                      />
                      {touched.website && errors.website && (
                        <Text fontSize="sm" color="red.500">
                          {errors.website}
                        </Text>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  {/* Address + Country */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={touched.Address && !!errors.Address}>
                      <FormLabel fontSize="sm" color="gray.600">
                        Address
                      </FormLabel>
                      <Input
                        name="Address"
                        placeholder="Address"
                        value={values.Address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="filled"
                        bg="gray.100"
                        _focus={{ bg: 'white', borderColor: 'purple.400' }}
                      />
                      {touched.Address && errors.Address && (
                        <Text fontSize="sm" color="red.500">
                          {errors.Address}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={touched.country && !!errors.country}>
                      <FormLabel fontSize="sm" color="gray.600">
                        Country
                      </FormLabel>
                      <Input
                        name="country"
                        placeholder="Country"
                        value={values.country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        variant="filled"
                        bg="gray.100"
                        _focus={{ bg: 'white', borderColor: 'purple.400' }}
                      />
                      {touched.country && errors.country && (
                        <Text fontSize="sm" color="red.500">
                          {errors.country}
                        </Text>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  {/* Plan selection */}
                  <Box>
                    <FormLabel fontSize="sm" color="gray.600" mb={3}>
                      Choose a plan
                    </FormLabel>
                    <SimpleGrid columns={{ base: 1, md: plans.length }} spacing={4}>
                      {plans.map((plan) => {
                        const isSelected = String(values.plan_id) === String(plan.plan_id);
                        return (
                          <Box
                            key={plan.plan_id}
                            onClick={() => setFieldValue('plan_id', plan.plan_id)}
                            cursor="pointer"
                            borderWidth="2px"
                            borderColor={isSelected ? 'purple.500' : 'gray.200'}
                            bg={isSelected ? 'purple.50' : 'white'}
                            borderRadius="lg"
                            p={5}
                            position="relative"
                            transition="0.2s"
                            _hover={{ borderColor: 'purple.400', transform: 'translateY(-2px)' }}
                          >
                            {isSelected && (
                              <Icon
                                as={CheckCircleIcon}
                                color="purple.500"
                                position="absolute"
                                top={3}
                                right={3}
                                boxSize={5}
                              />
                            )}
                            <Text fontWeight="bold" fontSize="lg" color="purple.700">
                              {plan.name}
                            </Text>
                            <Flex align="baseline" mt={1} mb={3}>
                              <Text fontSize="2xl" fontWeight="bold">
                                {plan.price === 0 ? 'Free' : `$${plan.price}`}
                              </Text>
                              {plan.period && (
                                <Text fontSize="sm" color="gray.500" ml={2}>
                                  {plan.period}
                                </Text>
                              )}
                            </Flex>
                            {plan.price === 0 && (
                              <Badge colorScheme="green" mb={2}>
                                No credit card required
                              </Badge>
                            )}
                            <VStack align="start" spacing={1} mt={2}>
                              {(plan.features || []).map((feature, i) => (
                                <Text key={i} fontSize="sm" color="gray.600">
                                  &#10003; {feature}
                                </Text>
                              ))}
                            </VStack>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                    {touched.plan_id && errors.plan_id && (
                      <Text fontSize="sm" color="red.500" mt={2}>
                        {errors.plan_id}
                      </Text>
                    )}
                  </Box>

                  <Button
                    type="submit"
                    w="100%"
                    mt={2}
                    rounded="2"
                    colorScheme="purple"
                    size="lg"
                    isLoading={isSubmitting}
                    transition="0.2s"
                    _hover={{ transform: 'scale(1.02)', shadow: 'md' }}
                  >
                    Create organization
                  </Button>

                  <Text fontSize="sm" textAlign="center" color="gray.500">
                    Already have an account?{' '}
                    <Text
                      as="span"
                      color="purple.600"
                      cursor="pointer"
                      fontWeight="medium"
                      onClick={() => navigate('/login')}
                    >
                      Sign in
                    </Text>
                  </Text>
                </VStack>
              </form>
            )}
          </Formik>
        </Box>
      </Card>
    </Flex>
  );
}